import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Divider,
  Skeleton,
  Steps,
  Empty,
  Modal,
  Spin,
} from "antd";
import {
  FormPHL,
  FormUSA,
  FormTTO,
  FormPOL,
  FormMEX,
  FormPAN,
  FormCAN,
  FormHKG,
  FormIND,
  FormCOL,
  FormEGY,
  FormAUS,
} from "./Forms";
import Documents from "./Documents";
import { MsgBox, isMobile } from "../../../shared/custom-ant-d";
import { AutoContainer, CustomAlertModal } from "../../../shared";
import { GlobalApiErrorHandler } from "../../../shared/helpers";
import { Steps as StepsMV } from "antd-mobile";
import { AESEncrypt, AESDecrypt } from "../../../shared/Crypto";
import { W4Form } from "./Documents/_W4Form";
import {
  reqGetDPCSText,
  reqGetRetrieveOnboardingLookupData,
  reqGetListOnboarding,
  reqPostSaveOnboarding,
} from "../../../axios/api";

const { Step } = Steps;
const steps = [
  {
    title: "Tell us about yourself",
    content: "Tell us about yourself",
  },
  {
    title: "Pre-Onboarding Documents",
    content: "Pre-Onboarding Documents",
  },
];

const validateMessages = {
  required: "${label} is required.",
};

const errMsgs = {
  default: "You currently don’t have any pre-onboarding activities.",
  offerNotAccepted:
    'Your offer must be accepted prior to beginning your Pre-Onboarding activities.  Please click on the "My Offers" link to accept the offer.',
};

export const PreOnboarding = () => {
  const [form] = Form.useForm();
  const Auth = useSelector((state) => state.Auth);

  const [currentStep, setCurrentStep] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [disableAll, setDisableAll] = useState(false);
  const [formData, setFormData] = useState(null);
  const [allCountryList, setAllCountryList] = useState(null);
  const [countryList, setCountryList] = useState(null);
  const [disabilityStatusList, setDisabilityStatusList] = useState(null);
  const [
    emergencyContactRelationshipList,
    setEmergencyContactRelationshipList,
  ] = useState(null);
  const [beneficiaryRelationshipList, setBeneficiaryRelationshipList] =
    useState(null);
  const [ethnicityList, setEthnicityList] = useState(null);
  const [genderList, setGenderList] = useState(null);
  const [languageList, setLanguageList] = useState(null);
  const [maritalStatusList, setMaritalStatusList] = useState(null);
  const [nameSuffixList, setNameSuffixList] = useState(null);
  const [raceList, setRaceList] = useState(null);
  const [salutationList, setSalutationList] = useState(null);
  const [statesList, setStatesList] = useState(null);
  const [usGenderList, setUsGenderList] = useState(null);
  const [mexicoMunicipalityList, setMexicoMunicipalityList] = useState(null);
  const [veteranList, setVeteranList] = useState(null);
  const [egyptReligionList, setEgyptReligionList] = useState(null);
  const [egyptMilitaryStatusList, setEgyptMilitaryStatusList] = useState(null);
  const [egyptCityTownList, setEgyptCityTownList] = useState(null);
  const [showButtons, setShowButtons] = useState(true);
  const [onboardingDocumentList, setOnboardingDocumentList] = useState(null);
  const [showW4Form, setShowW4Form] = useState(false);
  const [errMsg, setErrMsg] = useState(errMsgs.default);
  const [isLoading, setIsLoading] = useState(false);
  const [saveData, setSaveData] = useState(null);
  const [showModalProceed, setShowModalProceed] = useState(false);
  const [termContent, setTermContent] = useState("");
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);

  useEffect(() => {
    if (Auth.candidateID != "") {
      fetchListOnboarding();
    }
  }, [Auth]);

  useEffect(() => {
    if (formData != null && !pageLoading) {
      form.setFieldsValue(formData);
      if (formData.EmergencyContactList.length > 0) {
        form.setFieldsValue({
          EmergencyContactRelationID1:
            formData.EmergencyContactList[0].EmergencyContactRelationID,
          EmergencyContactRelationName1:
            formData.EmergencyContactList[0].EmergencyContactRelationName,
          EmergencyContactName1:
            formData.EmergencyContactList[0].EmergencyContactName,
          EmergencyContactPhone1:
            formData.EmergencyContactList[0].EmergencyContactPhone,
          EmergencyContactAltPhone1:
            formData.EmergencyContactList[0].EmergencyContactAltPhone,
          EmergencyContactEmail1:
            formData.EmergencyContactList[0].EmergencyContactEmail,
          EmergencyContactRelationID2:
            formData.EmergencyContactList[1]?.EmergencyContactRelationID,
          EmergencyContactRelationName2:
            formData.EmergencyContactList[1]?.EmergencyContactRelationName,
          EmergencyContactName2:
            formData.EmergencyContactList[1]?.EmergencyContactName,
          EmergencyContactPhone2:
            formData.EmergencyContactList[1]?.EmergencyContactPhone,
          EmergencyContactAltPhone2:
            formData.EmergencyContactList[1]?.EmergencyContactAltPhone,
          EmergencyContactEmail2:
            formData.EmergencyContactList[1]?.EmergencyContactEmail,
        });
      }
    }
  }, [formData, pageLoading]);

  useEffect(() => {
    if (formData != null) {
      setPageLoading(true);
      reqGetRetrieveOnboardingLookupData(formData.JobReqCountryID)
        .then((res) => {
          setAllCountryList(res.data.allCountryList);
          setCountryList(res.data.countryList);
          setDisabilityStatusList(res.data.disabilityStatusList);
          setEmergencyContactRelationshipList(
            res.data.emergencyContactRelationshipList
          );
          setBeneficiaryRelationshipList(res.data.beneficiaryRelationshipList);
          setEthnicityList(res.data.ethnicityList);
          setGenderList(res.data.genderList);
          setLanguageList(res.data.languageList);
          setMaritalStatusList(res.data.maritalStatusList);
          setNameSuffixList(res.data.nameSuffixList);
          setRaceList(res.data.raceList);
          setSalutationList(res.data.salutationList);
          setStatesList(res.data.statesList);
          setUsGenderList(res.data.usGenderList);
          setMexicoMunicipalityList(res.data.mexicoMunicipalityList);
          setVeteranList(res.data.veteranList);
          setEgyptReligionList(res.data.egyptReligionList);
          setEgyptMilitaryStatusList(res.data.egyptMilitaryStatusList);
          setEgyptCityTownList(res.data.egyptCityTownList);
          setPageLoading(false);
        })
        .catch((err) => {
          console.error(err);
          GlobalApiErrorHandler({ errors: err.response.data.errors });
        });
    }
  }, [formData]);

  const formProps = {
    form,
    disableAll,
    allCountryList,
    countryList,
    disabilityStatusList,
    emergencyContactRelationshipList,
    ethnicityList,
    beneficiaryRelationshipList,
    genderList,
    languageList,
    maritalStatusList,
    nameSuffixList,
    raceList,
    salutationList,
    statesList,
    usGenderList,
    mexicoMunicipalityList,
    veteranList,
    egyptReligionList,
    egyptMilitaryStatusList,
    egyptCityTownList,
  };

  const fetchListOnboarding = () => {
    setPageLoading(true);
    reqGetListOnboarding(Auth.candidateID)
      .then((res) => {
        if (res.data.isSuccess) {
          var dataList = JSON.parse(AESDecrypt(res.data.onboardingListEncJSON));
          if (dataList.length > 0) {
            var data = dataList[0];
            if (data.DependentList !== undefined) {
              for (var i = 0; i < data.DependentList.length; i++) {
                data.DependentList[i].DOB =
                  data.DependentList[i].DOB === null ||
                  data.DependentList[i].DOB === undefined
                    ? ""
                    : moment(data.DependentList[i].DOB);
              }
            }

            data = {
              ...data,
              ...{
                DOB:
                  data.DOB === null || data.DOB === undefined
                    ? ""
                    : moment(data.DOB),
              },
            };

            setFormData(data);
            setOnboardingDocumentList(data.OnboardingDocumentList);
            setDisableAll(data.FieldsCompletedInd);

            if (data.OfferAcceptedInd === false) {
              setFormData(null);
              setErrMsg(errMsgs.offerNotAccepted);
              return;
            }
            //setDisableAll(false);
          }
        } else {
          MsgBox({
            type: "error",
            message: "",
            description: "There are no on-boarding data for this account.",
          });
        }
        setPageLoading(false);
      })
      .catch((err) => {
        console.error(err);
        GlobalApiErrorHandler({ errors: err.response.data.errors });
      });
  };

  const docProps = {
    form,
    formData,
    onboardingDocumentList,
    setOnboardingDocumentList,
    setShowButtons,
    setShowW4Form,
    fetchListOnboarding,
  };

  const onFinish = (values) => {
    const emergencyContactList = [
      {
        OnboardingID: formData.OnboardingID,
        EmergencyContactRelationID: values.EmergencyContactRelationID1,
        EmergencyContactRelationName: values.EmergencyContactRelationName1,
        EmergencyContactName: values.EmergencyContactName1,
        EmergencyContactPhone: values.EmergencyContactPhone1,
        EmergencyContactAltPhone: values.EmergencyContactAltPhone1,
        EmergencyContactEmail: values.EmergencyContactEmail1,
      },
    ];

    if (
      values.EmergencyContactRelationName2 != null &&
      values.EmergencyContactName2 != null &&
      values.EmergencyContactPhone2 != null &&
      values.EmergencyContactAltPhone2 &&
      values.EmergencyContactEmail2
    ) {
      emergencyContactList.push({
        OnboardingID: formData.OnboardingID,
        EmergencyContactRelationID: values.EmergencyContactRelationID2,
        EmergencyContactRelationName: values.EmergencyContactRelationName2,
        EmergencyContactName: values.EmergencyContactName2,
        EmergencyContactPhone: values.EmergencyContactPhone2,
        EmergencyContactAltPhone: values.EmergencyContactAltPhone2,
        EmergencyContactEmail: values.EmergencyContactEmail2,
      });
    }

    delete values.EmergencyContactRelationID1;
    delete values.EmergencyContactRelationName1;
    delete values.EmergencyContactName1;
    delete values.EmergencyContactPhone1;
    delete values.EmergencyContactAltPhone1;
    delete values.EmergencyContactEmail1;
    delete values.EmergencyContactRelationID2;
    delete values.EmergencyContactRelationName2;
    delete values.EmergencyContactName2;
    delete values.EmergencyContactPhone2;
    delete values.EmergencyContactAltPhone2;
    delete values.EmergencyContactEmail2;

    if (values.DependentList !== undefined) {
      for (var i = 0; i < values.DependentList.length; i++) {
        values.DependentList[i].BeneficiaryPercentage = parseFloat(
          values.DependentList[i].BeneficiaryPercentage ?? 0
        );

        const dependentRelationName =
          values.DependentList[i].DependentRelationName;
        const label = beneficiaryRelationshipList.filter(
          (i) => i.expText == dependentRelationName
        );

        values.DependentList[i].DependentRelationID = label[0]?.codeValueID;
      }
    }

    var payload = { ...formData, ...values };
    payload.EmergencyContactList = emergencyContactList;

    setSaveData(payload);
    setShowModalProceed(true);
  };

  const onFinishFailed = (errorInfo) => {
    console.error("Failed:", errorInfo);
    form.scrollToField(errorInfo.errorFields[0].name);
  };

  const showTermsModal = (e) => {
    e.preventDefault();
    setIsTermsModalVisible(true);

    if (!termContent && Auth.countryID) {
      reqGetDPCSText(Auth.countryID)
        .then((res) => {
          setTermContent(res.data);
        })
        .catch((err) => {
          console.log(err);
          setTermContent("Unable to load the data privacy statement. Please try again later.");
        });
    }
  };

  const handleCloseTermsModal = (e) => {
    if (e) {
      e.preventDefault();
    }

    setIsTermsModalVisible(false);
  };

  const onSubmit = (payload) => {
    setIsLoading(true);
    var payloadStr = JSON.stringify({
      onboardingObj: null,
      onboardingEncJSON: AESEncrypt(JSON.stringify(payload)),
    });

    reqPostSaveOnboarding(payloadStr)
      .then((res) => {
        if (res.data.isSuccess) {
          MsgBox({
            type: "success",
            message: "",
            description: "Pre-Onboarding has been successfully saved.",
          });
          setDisableAll(true);
          setCurrentStep(currentStep + 1);
        } else {
          MsgBox({
            type: "error",
            message: "",
            description: res.data.errorMessage,
          });
        }
      })
      .catch((err) => {
        console.log(err);
        GlobalApiErrorHandler({ errors: err.response.data.errors });
      })
      .finally(() => {
        setSaveData(null);
        setIsLoading(false);
      });
  };

  return (
    <>
      <CustomAlertModal
        type="confirm"
        content="Are you sure you want to save and proceed?"
        onCancel={() => setShowModalProceed(false)}
        visible={showModalProceed}
        destroyOnClose={true}
        footer={[
          <Button
            key="yes"
            onClick={(e) => {
              e.preventDefault();
              onSubmit(saveData);
              setShowModalProceed(false);
            }}
            type="danger"
          >
            Yes
          </Button>,
          <Button
            key="no"
            onClick={(e) => setShowModalProceed(false)}
            type="danger"
          >
            No
          </Button>,
        ]}
      />
      <Form
        name="preOnboarding"
        initialValues={{
          remember: true,
        }}
        form={form}
        scrollToFirstError={true}
        validateMessages={validateMessages}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout={"vertical"}
        size={"small"}
      >
        <Row justify={"center"}>
          <Col md={24} xs={24}>
            <Card
              title={"Pre-Onboarding"}
              className="documents-card iqor-header"
            >
              <Skeleton loading={pageLoading} active>
                <>
                  {formData == null ? (
                    <Empty description={errMsg} />
                  ) : (
                    <>
                      {isMobile ? (
                        <StepsMV current={currentStep}>
                          {steps.map((item) => (
                            <Step key={item.title} title={item.title} />
                          ))}
                        </StepsMV>
                      ) : (
                        <>
                          <Steps current={currentStep}>
                            {steps.map((item) => (
                              <Step key={item.title} title={item.title} />
                            ))}
                          </Steps>
                          <br />
                        </>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "USA" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormUSA {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "TTO" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormTTO {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "PHL" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormPHL {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "POL" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormPOL {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "MEX" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormMEX {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "PAN" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormPAN {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "CAN" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormCAN {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "HKG" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormHKG {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "IND" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormIND {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "EGY" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormEGY {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "AUS" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormAUS {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form &&
                      currentStep == 0 &&
                      formData?.JobReqCountryCode == "COL" &&
                      !pageLoading ? (
                        <AutoContainer percent={0.48}>
                          <FormCOL {...formProps} />
                        </AutoContainer>
                      ) : (
                        <></>
                      )}
                      {!showW4Form && currentStep == 1 && !pageLoading ? (
                        <Documents {...docProps} />
                      ) : (
                        <></>
                      )}
                      {showW4Form && currentStep == 1 && !pageLoading ? (
                        <W4Form {...docProps} />
                      ) : (
                        <></>
                      )}

                    </>
                  )}
                </>
              </Skeleton>
              <Row>
                <Col md={24}>
                  <Divider orientation="left"></Divider>
                </Col>
              </Row>
              <Row justify="center">
                <Col md={24} sm={23} xs={24}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                      <span>Terms of Use: <a href="/" onClick={showTermsModal}>Data Privacy</a></span>
                    </div>
                    <div>
                      {!disableAll && showButtons && formData !== null ? (
                        <Button
                          loading={isLoading}
                          type="danger"
                          htmlType="submit"
                        >
                          {" "}
                          Save and Proceed
                        </Button>
                      ) : (
                        <></>
                      )}
                      {disableAll && currentStep == 0 && showButtons ? (
                        <Button
                          type="danger"
                          htmlType="button"
                          onClick={() => {
                            setCurrentStep(currentStep + 1);
                          }}
                        >
                          Proceed
                        </Button>
                      ) : (
                        <></>
                      )}
                      {disableAll &&
                      currentStep == 1 &&
                      showButtons &&
                      !showW4Form ? (
                        <Button
                          type="danger"
                          htmlType="button"
                          onClick={() => {
                            setCurrentStep(currentStep - 1);
                          }}
                        >
                          Previous
                        </Button>
                      ) : (
                        <></>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </Form>

      <Modal
        title="Data Privacy Consent Statement"
        visible={isTermsModalVisible}
        onCancel={handleCloseTermsModal}
        className="terms-modal"
        destroyOnClose
        width={850}
        footer={null}
      >
        <div id="terms-content">
          {termContent ? <div dangerouslySetInnerHTML={{ __html: termContent }}></div> : <Spin />}
        </div>
      </Modal>
    </>
  );
};
